// src/pages/ClientSessionHub.js
// Client-facing session hub — Sessions, Messages, Favorites tabs.
// Route: /my-sessions (authenticated, wrapped in Layout)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { collectionGroup, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { cancelEventRegistration } from '../services/event-service';
import { Calendar, Monitor, MapPin } from 'lucide-react';

const REGION = 'us-west1';

const formatDateTime = (dateTime, timezone) => {
  try {
    const date = dateTime?._seconds ? new Date(dateTime._seconds * 1000) : new Date(dateTime);
    return date.toLocaleString('en-US', {
      timeZone: timezone || 'UTC', weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return String(dateTime); }
};

const formatShortDate = (dateTime, timezone) => {
  try {
    const date = dateTime?._seconds ? new Date(dateTime._seconds * 1000) : new Date(dateTime);
    return date.toLocaleDateString('en-US', { timeZone: timezone || 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

// ── Booking Card ──

const BookingCard = ({ booking, navigate, isUpcoming, isFavorited, onToggleFavorite }) => {
  const hasProposal = booking.status === 'reschedule_proposed' && booking.rescheduleProposal?.status === 'pending';
  const dateStr = formatDateTime(booking.dateTime, booking.timezone);
  const price = booking.service?.price ? `$${booking.service.price}` : 'Free';
  const duration = booking.service?.duration || 60;

  const statusConfig = {
    confirmed: { label: 'Confirmed', bg: '#ECFDF5', color: '#065F46' },
    reschedule_proposed: { label: 'Reschedule proposed', bg: '#FEF3C7', color: '#92400E' },
    cancelled: { label: 'Cancelled', bg: '#FEF2F2', color: '#991B1B' },
  };
  const isCancelled = booking.status === 'cancelled';
  const status = statusConfig[booking.status] || { label: 'Completed', bg: '#F3F4F6', color: '#374151' };

  // For past non-cancelled bookings, show "Completed"
  const displayStatus = (!isUpcoming && !isCancelled) ? { label: 'Completed', bg: '#F3F4F6', color: '#6B7280' } : status;

  return (
    <div style={{
      background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px',
      padding: '20px', marginBottom: '12px',
      opacity: isUpcoming ? 1 : 0.85,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
        {/* Practitioner avatar */}
        <div style={{
          width: '44px', height: '44px', minWidth: '44px', borderRadius: '50%', flexShrink: 0,
          background: booking.practitionerPhoto
            ? `url(${booking.practitionerPhoto}) center/cover`
            : 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
          backgroundSize: booking.practitionerPhoto ? 'cover' : '400% 400%',
          animation: booking.practitionerPhoto ? 'none' : 'auroraShift 16s ease infinite',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '16px', fontWeight: 600, color: '#fff',
        }}>
          {!booking.practitionerPhoto && (booking.practitionerName?.[0] || '?')}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', margin: 0 }}>
              {booking.service?.name || 'Session'}
            </p>
            <span style={{
              fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
              background: displayStatus.bg, color: displayStatus.color, whiteSpace: 'nowrap', flexShrink: 0,
            }}>{displayStatus.label}</span>
          </div>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            with {booking.practitionerName}
            {onToggleFavorite && (
              <span role="button" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(booking.practitionerId, booking); }}
                onKeyDown={(e) => e.key === 'Enter' && onToggleFavorite(booking.practitionerId, booking)}
                style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', lineHeight: 1 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill={isFavorited ? '#bb9749' : 'none'} stroke={isFavorited ? '#bb9749' : '#D1D5DB'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              </span>
            )}
          </p>
          <p style={{ fontSize: '13px', color: '#9CA3AF', margin: '0 0 2px' }}>{dateStr}</p>
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>{duration} min · {price}</p>

          {/* Cancelled refund info */}
          {isCancelled && booking.refundAmount > 0 && booking.refundStatus !== 'failed' && (
            <p style={{ fontSize: '12px', color: '#065F46', marginTop: '6px' }}>
              Refund of ${(booking.refundAmount / 100).toFixed(2)} {booking.refundStatus === 'processed' ? 'processed' : 'processing'}
            </p>
          )}
          {isCancelled && booking.refundStatus === 'failed' && (
            <p style={{ fontSize: '12px', color: '#92400E', marginTop: '6px' }}>
              Refund processing — contact support if not received within 10 business days
            </p>
          )}
        </div>
      </div>

      {/* Proposal banner */}
      {hasProposal && (
        <div style={{
          marginTop: '12px', padding: '10px 14px', borderRadius: '8px',
          background: '#FFFBEB', border: '1px solid #FDE68A',
        }}>
          <p style={{ fontSize: '13px', color: '#92400E', margin: 0 }}>
            {booking.practitionerName} wants to reschedule —{' '}
            <span
              role="button" tabIndex={0}
              onClick={() => navigate(`/booking/proposal/${booking.id}/${booking.managementToken}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/booking/proposal/${booking.id}/${booking.managementToken}`)}
              style={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', userSelect: 'none' }}
            >respond now</span>
          </p>
        </div>
      )}

      {/* Shared notes */}
      {!isUpcoming && booking.sharedNotes && (
        <div style={{
          marginTop: '12px', padding: '14px 16px', borderRadius: '8px',
          background: '#F9FAFB', border: '1px solid #F3F4F6',
        }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: '#bb9749', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>📝</span> Notes from {booking.practitionerName}
          </p>
          <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.6, margin: 0 }}>
            {booking.sharedNotes.content}
          </p>
          {booking.sharedNotes.themes?.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
              {booking.sharedNotes.themes.map(t => (
                <span key={t} style={{
                  padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 500,
                  background: '#F3F0FF', color: '#7c3aed',
                }}>{t}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recording link */}
      {!isUpcoming && booking.recordingUrl && (
        <div style={{
          marginTop: '8px', padding: '10px 14px', borderRadius: '8px',
          background: '#f5f3ff', border: '1px solid #ede9fe',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <span style={{ fontSize: '14px' }}>🎥</span>
          <a href={booking.recordingUrl} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '13px', fontWeight: 500, color: '#7c3aed', textDecoration: 'none' }}
          >View session recording</a>
        </div>
      )}

      {/* Action buttons */}
      {isUpcoming && !isCancelled && booking.managementToken && booking.status !== 'reschedule_proposed' && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
          <span role="button" tabIndex={0}
            onClick={() => navigate(`/booking/manage/${booking.id}/${booking.managementToken}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/booking/manage/${booking.id}/${booking.managementToken}`)}
            style={{ padding: '7px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 500, background: 'transparent', color: '#bb9749', border: '1px solid #bb9749', cursor: 'pointer', userSelect: 'none' }}
          >Reschedule</span>
          <span role="button" tabIndex={0}
            onClick={() => navigate(`/booking/manage/${booking.id}/${booking.managementToken}?action=cancel`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/booking/manage/${booking.id}/${booking.managementToken}?action=cancel`)}
            style={{ padding: '7px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 500, background: 'transparent', color: '#DC2626', border: '1px solid #FCA5A5', cursor: 'pointer', userSelect: 'none' }}
          >Cancel</span>
          {booking.practitionerSlug && (
            <span role="button" tabIndex={0}
              onClick={() => navigate(`/site/${booking.practitionerSlug}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/site/${booking.practitionerSlug}`)}
              style={{ padding: '7px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, background: '#bb9749', color: '#fff', cursor: 'pointer', userSelect: 'none', marginLeft: 'auto' }}
            >Book again</span>
          )}
        </div>
      )}

      {/* Book again for past sessions */}
      {!isUpcoming && !isCancelled && booking.practitionerSlug && (
        <div style={{ marginTop: '12px', textAlign: 'right' }}>
          <span role="button" tabIndex={0}
            onClick={() => navigate(`/site/${booking.practitionerSlug}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/site/${booking.practitionerSlug}`)}
            style={{ padding: '7px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, background: '#bb9749', color: '#fff', cursor: 'pointer', userSelect: 'none', display: 'inline-block' }}
          >Book again</span>
        </div>
      )}
    </div>
  );
};

// ── Event Card for Session Hub ──

const EventSessionCard = ({ event, registration, navigate, isUpcoming, onCancel }) => {
  const [cancelling, setCancelling] = useState(false);

  const formatTime12 = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  let dateLabel = '';
  if (event.eventType === 'single' && event.schedule?.date) {
    const d = new Date(event.schedule.date + 'T12:00:00');
    dateLabel = `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} • ${formatTime12(event.schedule.startTime)} — ${formatTime12(event.schedule.endTime)}`;
  } else if (event.eventType === 'series' && event.schedule?.sessionDates?.length > 0) {
    dateLabel = `${event.schedule.sessionDates.length} sessions starting ${new Date(event.schedule.sessionDates[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  } else if (event.eventType === 'multi_day' && event.schedule?.date) {
    const s = new Date(event.schedule.date + 'T12:00:00');
    const e2 = new Date(event.schedule.endDate + 'T12:00:00');
    dateLabel = `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${e2.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }

  const handleCancel = async () => {
    if (!window.confirm(`Cancel your registration for "${event.title}"?`)) return;
    setCancelling(true);
    try {
      await cancelEventRegistration(event.practitionerId, event.id, registration.id);
      onCancel?.(registration.id);
    } catch (err) {
      alert('Failed to cancel: ' + (err.message || 'Unknown error'));
    } finally {
      setCancelling(false);
    }
  };

  const detailUrl = event.practitionerSlug && event.slug
    ? `/site/${event.practitionerSlug}/events/${event.slug}`
    : null;

  return (
    <div style={{
      background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px',
      padding: '18px 20px', marginBottom: '10px',
    }}>
      {/* EVENT badge + title */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
        <span style={{
          padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
          background: '#F5F3FF', color: '#7c3aed', textTransform: 'uppercase',
          letterSpacing: '0.05em', flexShrink: 0, marginTop: 3,
        }}>Event</span>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.3, fontFamily: "'Montserrat', sans-serif" }}>
          {event.title}
        </h3>
      </div>

      {/* Practitioner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        {event.practitionerPhoto ? (
          <img src={event.practitionerPhoto} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #7c3aed, #22d3ee)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700 }}>
            {(event.practitionerName || '?')[0].toUpperCase()}
          </div>
        )}
        <span style={{ fontSize: 13, color: '#6B7280', fontFamily: "'Montserrat', sans-serif" }}>{event.practitionerName}</span>
      </div>

      {/* Date + format */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Calendar size={13} color="#9CA3AF" />
        <span style={{ fontSize: 13, color: '#374151', fontFamily: "'Montserrat', sans-serif" }}>{dateLabel}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
        {event.format === 'virtual' || event.format === 'hybrid' ? <Monitor size={13} color="#9CA3AF" /> : <MapPin size={13} color="#9CA3AF" />}
        <span style={{ fontSize: 13, color: '#6B7280', fontFamily: "'Montserrat', sans-serif" }}>
          {event.format === 'virtual' ? 'Virtual' : event.format === 'in_person' ? 'In-Person' : 'Hybrid'}
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {isUpcoming && detailUrl && (
          <span role="button" tabIndex={0}
            onClick={() => navigate(detailUrl)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(detailUrl)}
            style={{ fontSize: 13, fontWeight: 600, color: '#bb9749', cursor: 'pointer', userSelect: 'none', fontFamily: "'Montserrat', sans-serif" }}>
            View event
          </span>
        )}
        {isUpcoming && (
          <span role="button" tabIndex={0}
            onClick={handleCancel}
            onKeyDown={(e) => e.key === 'Enter' && handleCancel()}
            style={{ fontSize: 13, fontWeight: 500, color: '#DC2626', cursor: cancelling ? 'default' : 'pointer', userSelect: 'none', opacity: cancelling ? 0.5 : 1, fontFamily: "'Montserrat', sans-serif" }}>
            {cancelling ? 'Cancelling...' : 'Cancel registration'}
          </span>
        )}
        {!isUpcoming && (
          <span style={{ fontSize: 13, color: '#6B7280', fontStyle: 'italic', fontFamily: "'Montserrat', sans-serif" }}>Attended</span>
        )}
        {!isUpcoming && detailUrl && (
          <span role="button" tabIndex={0}
            onClick={() => navigate(detailUrl)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(detailUrl)}
            style={{ fontSize: 13, fontWeight: 500, color: '#bb9749', cursor: 'pointer', userSelect: 'none', fontFamily: "'Montserrat', sans-serif" }}>
            View details
          </span>
        )}
      </div>
    </div>
  );
};

// ── Main Component ──

const ClientSessionHub = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'saved' ? 'saved' : 'sessions';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ serviceNames: [], practitionerIds: [] });
  const [serviceFilter, setServiceFilter] = useState('');
  const [practitionerFilter, setPractitionerFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState('');
  const [userInitials, setUserInitials] = useState('');

  // Saved practitioners (loaded eagerly for heart icons on sessions)
  const [savedPractitioners, setSavedPractitioners] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedLoaded, setSavedLoaded] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  // Registered events
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);

  // Get user info
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        const name = user.displayName || user.email?.split('@')[0] || 'there';
        setUserName(name);
        setUserInitials(
          user.displayName
            ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
            : (user.email?.[0] || '?').toUpperCase()
        );
      }
    });
    return () => unsub();
  }, []);

  // Fetch bookings
  const fetchBookings = useCallback(async (startAfter = null, append = false) => {
    if (startAfter) setLoadingMore(true); else setLoading(true);
    try {
      const functions = getFunctions(undefined, REGION);
      const fn = httpsCallable(functions, 'getClientBookings');
      const filter = {};
      if (serviceFilter) filter.serviceName = serviceFilter;
      if (practitionerFilter) filter.practitionerId = practitionerFilter;
      const result = await fn({ limit: 5, startAfter, filter: (serviceFilter || practitionerFilter) ? filter : null });
      const data = result.data;
      if (append) {
        setPast(prev => [...prev, ...(data.past || [])]);
      } else {
        setUpcoming(data.upcoming || []);
        setPast(data.past || []);
        setFilterOptions(data.filters || { serviceNames: [], practitionerIds: [] });
      }
      setHasMore(data.hasMore || false);
    } catch (err) {
      console.error('Failed to load bookings:', err);
      if (!append) setError('Unable to load your sessions.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [serviceFilter, practitionerFilter]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Fetch registered events
  useEffect(() => {
    if (!db) return;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      try {
        const regsSnap = await getDocs(query(
          collectionGroup(db, 'registrations'),
          where('attendeeId', '==', user.uid),
          where('status', '==', 'registered'),
        ));

        const today = new Date().toISOString().split('T')[0];
        const practCache = {};
        const evts = [];

        for (const regDoc of regsSnap.docs) {
          const reg = { id: regDoc.id, ...regDoc.data() };
          const pathParts = regDoc.ref.path.split('/');
          const practitionerId = pathParts[1];
          const eventId = pathParts[3];

          // Load event
          try {
            const eventSnap = await getDoc(doc(db, 'practitioners', practitionerId, 'events', eventId));
            if (!eventSnap.exists()) continue;
            const evt = { id: eventSnap.id, ...eventSnap.data(), practitionerId };

            // Load practitioner info (cached)
            if (!practCache[practitionerId]) {
              const practSnap = await getDoc(doc(db, 'practitioners', practitionerId));
              if (practSnap.exists()) {
                const pd = practSnap.data();
                const pub = pd.website_published || {};
                practCache[practitionerId] = {
                  name: pub.profile?.displayName || pd.displayName || pd.name || '',
                  photo: pub.profile?.photo || null,
                  slug: pd.slug || pub.profile?.slug || '',
                };
              } else {
                practCache[practitionerId] = { name: '', photo: null, slug: '' };
              }
            }
            evt.practitionerName = practCache[practitionerId].name;
            evt.practitionerPhoto = practCache[practitionerId].photo;
            evt.practitionerSlug = practCache[practitionerId].slug;
            evt._registration = reg;
            evt._sortDate = evt.eventType === 'single' ? evt.schedule?.date : evt.eventType === 'series' ? evt.schedule?.sessionDates?.[0] : evt.schedule?.date;
            evt._endDate = evt.eventType === 'single' ? evt.schedule?.date : evt.eventType === 'series' ? evt.schedule?.sessionDates?.[evt.schedule.sessionDates.length - 1] : evt.schedule?.endDate;

            evts.push(evt);
          } catch { /* skip */ }
        }

        const upE = evts.filter(e => (e._endDate || '9999') >= today).sort((a, b) => (a._sortDate || '').localeCompare(b._sortDate || ''));
        const pastE = evts.filter(e => (e._endDate || '9999') < today).sort((a, b) => (b._sortDate || '').localeCompare(a._sortDate || ''));
        setUpcomingEvents(upE);
        setPastEvents(pastE);
      } catch (err) {
        console.warn('Failed to load registered events:', err.message);
      }
    });
    return () => unsub();
  }, []);

  const handleEventCancelled = useCallback((regId) => {
    setUpcomingEvents(prev => prev.filter(e => e._registration?.id !== regId));
  }, []);

  // Load more past sessions
  const handleLoadMore = () => {
    if (past.length > 0) {
      fetchBookings(past[past.length - 1].id, true);
    }
  };

  // Saved practitioners
  useEffect(() => {
    if (!savedLoaded) {
      setSavedLoading(true);
      const functions = getFunctions(undefined, REGION);
      const fn = httpsCallable(functions, 'getSavedPractitioners');
      fn({}).then(result => {
        const saved = result.data.saved || [];
        setSavedPractitioners(saved);
        setSavedIds(new Set(saved.map(p => p.practitionerId)));
        setSavedLoaded(true);
      }).catch(() => {}).finally(() => setSavedLoading(false));
    }
  }, [savedLoaded]);

  const handleUnsave = async (practitionerId) => {
    try {
      const functions = getFunctions(undefined, REGION);
      const fn = httpsCallable(functions, 'unsavePractitioner');
      await fn({ practitionerId });
      setSavedPractitioners(prev => prev.filter(p => p.practitionerId !== practitionerId));
      setSavedIds(prev => { const next = new Set(prev); next.delete(practitionerId); return next; });
    } catch (err) { console.error('Unsave failed:', err); }
  };

  const handleToggleFavorite = async (practitionerId, booking) => {
    const functions = getFunctions(undefined, REGION);
    if (savedIds.has(practitionerId)) {
      setSavedIds(prev => { const next = new Set(prev); next.delete(practitionerId); return next; });
      setSavedPractitioners(prev => prev.filter(p => p.practitionerId !== practitionerId));
      httpsCallable(functions, 'unsavePractitioner')({ practitionerId }).catch(() => {
        setSavedIds(prev => { const next = new Set(prev); next.add(practitionerId); return next; });
      });
    } else {
      setSavedIds(prev => { const next = new Set(prev); next.add(practitionerId); return next; });
      httpsCallable(functions, 'savePractitioner')({
        practitionerId,
        name: booking?.practitionerName || 'Practitioner',
        slug: booking?.practitionerSlug || null,
      }).catch(() => {
        setSavedIds(prev => { const next = new Set(prev); next.delete(practitionerId); return next; });
      });
      // Reload saved list to get full data for Favorites tab
      setSavedLoaded(false);
    }
  };

  // Derive practitioners list for Messages tab
  const practitionersFromBookings = React.useMemo(() => {
    const allBookings = [...upcoming, ...past];
    const map = {};
    allBookings.forEach(b => {
      if (!map[b.practitionerId]) {
        map[b.practitionerId] = {
          id: b.practitionerId,
          name: b.practitionerName,
          photo: b.practitionerPhoto,
          slug: b.practitionerSlug,
          email: b.practitionerEmail,
          lastSession: b.dateTime,
          count: 0,
        };
      }
      map[b.practitionerId].count++;
      // Keep most recent session date
      const existing = map[b.practitionerId].lastSession?._seconds || 0;
      const current = b.dateTime?._seconds || 0;
      if (current > existing) map[b.practitionerId].lastSession = b.dateTime;
    });
    return Object.values(map).sort((a, b) => (b.lastSession?._seconds || 0) - (a.lastSession?._seconds || 0));
  }, [upcoming, past]);

  // Derive practitioner names for filter dropdown
  const practitionerNames = React.useMemo(() => {
    const all = [...upcoming, ...past];
    const map = {};
    all.forEach(b => { if (b.practitionerId) map[b.practitionerId] = b.practitionerName; });
    return map;
  }, [upcoming, past]);

  const tabs = [
    { id: 'sessions', label: 'Sessions' },
    { id: 'messages', label: 'Messages' },
    { id: 'saved', label: 'Favorites' },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.content}>
        {/* Welcome section */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
          <div style={{
            width: '48px', height: '48px', minWidth: '48px', borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
            backgroundSize: '400% 400%',
            animation: 'auroraShift 16s ease infinite',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '18px', fontWeight: 700, color: '#fff',
          }}>{userInitials}</div>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: '0 0 2px' }}>
              Welcome back, {userName}
            </h1>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
              Your journey on Divine Frameworks
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '32px', borderBottom: '1px solid #E5E7EB' }}>
          {tabs.map(tab => (
            <span
              key={tab.id}
              role="button" tabIndex={0}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => e.key === 'Enter' && setActiveTab(tab.id)}
              style={{
                padding: '12px 20px', background: 'none', border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #bb9749' : '2px solid transparent',
                fontSize: '14px', fontWeight: activeTab === tab.id ? 600 : 500,
                color: activeTab === tab.id ? '#111827' : '#6B7280',
                cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
                transition: 'all 0.2s ease', userSelect: 'none',
              }}
            >{tab.label}</span>
          ))}
        </div>

        {/* ── Sessions Tab ── */}
        {activeTab === 'sessions' && (
          <>
            {loading && (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={styles.spinner} />
                <p style={{ color: '#6B7280', fontSize: '14px', marginTop: '12px' }}>Loading your sessions...</p>
              </div>
            )}

            {error && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ color: '#991B1B', fontSize: '15px' }}>{error}</p>
              </div>
            )}

            {!loading && !error && upcoming.length === 0 && past.length === 0 && upcomingEvents.length === 0 && pastEvents.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✨</div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>No sessions yet</h3>
                <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px' }}>
                  Browse practitioners to book your first session.
                </p>
                <span role="button" tabIndex={0}
                  onClick={() => navigate('/directory')}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/directory')}
                  style={{ ...styles.goldBtn, display: 'inline-block', userSelect: 'none' }}
                >Browse practitioners</span>
              </div>
            )}

            {/* Upcoming events (before upcoming bookings) */}
            {upcomingEvents.length > 0 && (
              <div style={{ marginBottom: upcoming.length > 0 ? 0 : 32 }}>
                {upcoming.length === 0 && <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>Upcoming</h2>}
                {upcomingEvents.map(evt => (
                  <EventSessionCard key={`evt-${evt.id}`} event={evt} registration={evt._registration} navigate={navigate} isUpcoming={true} onCancel={handleEventCancelled} />
                ))}
              </div>
            )}

            {!loading && !error && (upcoming.length > 0 || past.length > 0 || upcomingEvents.length > 0 || pastEvents.length > 0) && (
              <>
                {/* Upcoming */}
                {upcoming.length > 0 && (
                  <div style={{ marginBottom: '32px' }}>
                    <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
                      Upcoming <span style={{ fontSize: '13px', fontWeight: 500, color: '#9CA3AF' }}>({upcoming.length})</span>
                    </h2>
                    {upcoming.map(b => <BookingCard key={b.id} booking={b} navigate={navigate} isUpcoming={true} isFavorited={savedIds.has(b.practitionerId)} onToggleFavorite={handleToggleFavorite} />)}
                  </div>
                )}

                {/* Past sessions */}
                {past.length > 0 && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                      <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#6B7280', margin: 0 }}>Past sessions</h2>
                      {/* Filters */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {filterOptions.serviceNames.length > 1 && (
                          <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}
                            style={styles.filterSelect}>
                            <option value="">All services</option>
                            {filterOptions.serviceNames.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                        {Object.keys(practitionerNames).length > 1 && (
                          <select value={practitionerFilter} onChange={(e) => setPractitionerFilter(e.target.value)}
                            style={styles.filterSelect}>
                            <option value="">All practitioners</option>
                            {Object.entries(practitionerNames).map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                    {past.map(b => <BookingCard key={b.id} booking={b} navigate={navigate} isUpcoming={false} isFavorited={savedIds.has(b.practitionerId)} onToggleFavorite={handleToggleFavorite} />)}
                    {hasMore && (
                      <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <span role="button" tabIndex={0}
                          onClick={handleLoadMore}
                          onKeyDown={(e) => e.key === 'Enter' && handleLoadMore()}
                          style={{
                            fontSize: '14px', fontWeight: 500, color: '#bb9749',
                            cursor: loadingMore ? 'default' : 'pointer', userSelect: 'none',
                          }}
                        >{loadingMore ? 'Loading...' : 'Show more'}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Past events */}
                {pastEvents.length > 0 && (
                  <div style={{ marginTop: past.length > 0 ? 0 : undefined }}>
                    {past.length === 0 && pastEvents.length > 0 && (
                      <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#6B7280', margin: '0 0 12px' }}>Past</h2>
                    )}
                    {pastEvents.map(evt => (
                      <EventSessionCard key={`evt-past-${evt.id}`} event={evt} registration={evt._registration} navigate={navigate} isUpcoming={false} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── Messages Tab ── */}
        {activeTab === 'messages' && (
          <>
            {practitionersFromBookings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>No messages yet</h3>
                <p style={{ fontSize: '14px', color: '#6B7280', maxWidth: '360px', margin: '0 auto', lineHeight: 1.6 }}>
                  After you book a session, you'll be able to reach your practitioners here.
                </p>
              </div>
            ) : (
              <div>
                {practitionersFromBookings.map(p => (
                  <div key={p.id} style={{
                    background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px',
                    padding: '16px 20px', marginBottom: '10px',
                    display: 'flex', alignItems: 'center', gap: '14px',
                  }}>
                    <div style={{
                      width: '44px', height: '44px', minWidth: '44px', borderRadius: '50%', flexShrink: 0,
                      background: p.photo
                        ? `url(${p.photo}) center/cover`
                        : 'linear-gradient(135deg, #7c3aed, #22d3ee)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '16px', fontWeight: 600, color: '#fff',
                    }}>
                      {!p.photo && (p.name?.[0] || '?')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', margin: '0 0 2px' }}>{p.name}</p>
                      <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
                        Last session: {formatShortDate(p.lastSession)} · {p.count} session{p.count !== 1 ? 's' : ''} total
                      </p>
                    </div>
                    <span role="button" tabIndex={0}
                      onClick={() => {
                        if (p.email) window.location.href = `mailto:${p.email}`;
                        else if (p.slug) navigate(`/site/${p.slug}`);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && (p.email ? (window.location.href = `mailto:${p.email}`) : p.slug && navigate(`/site/${p.slug}`))}
                      style={{
                        padding: '7px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                        background: 'transparent', color: '#bb9749', border: '1px solid #bb9749',
                        cursor: 'pointer', userSelect: 'none', flexShrink: 0,
                      }}
                    >Message</span>
                  </div>
                ))}
                <p style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic', textAlign: 'center', marginTop: '20px' }}>
                  Direct messaging is coming soon
                </p>
              </div>
            )}
          </>
        )}

        {/* ── Favorites Tab ── */}
        {activeTab === 'saved' && (
          <>
            {savedLoading && (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={styles.spinner} />
              </div>
            )}

            {!savedLoading && savedPractitioners.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>❤️</div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>No favorites yet</h3>
                <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px', maxWidth: '360px', margin: '0 auto 24px', lineHeight: 1.6 }}>
                  Browse practitioners and save the ones that resonate with you.
                </p>
                <span role="button" tabIndex={0}
                  onClick={() => navigate('/directory')}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/directory')}
                  style={{ ...styles.goldBtn, display: 'inline-block', userSelect: 'none' }}
                >Browse practitioners</span>
              </div>
            )}

            {!savedLoading && savedPractitioners.length > 0 && (
              <div className="df-favorites-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', alignItems: 'stretch' }}>
                {savedPractitioners.map(p => {
                  const visibleServices = (p.services || []).slice(0, 3);
                  const extraCount = (p.services || []).length - 3;
                  return (
                    <div key={p.practitionerId} style={{
                      background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px',
                      padding: '20px', display: 'flex', flexDirection: 'column', minHeight: '180px',
                    }}>
                      {/* Header: photo + name + tagline */}
                      <div style={{ display: 'flex', gap: '14px', marginBottom: '12px' }}>
                        <div style={{
                          width: '48px', height: '48px', minWidth: '48px', borderRadius: '50%', flexShrink: 0,
                          background: p.practitionerPhoto ? `url(${p.practitionerPhoto}) center/cover` : 'linear-gradient(135deg, #7c3aed, #22d3ee)',
                          backgroundSize: p.practitionerPhoto ? 'cover' : undefined,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '18px', fontWeight: 600, color: '#fff',
                        }}>
                          {!p.practitionerPhoto && (p.practitionerName?.[0] || '?')}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', margin: '0 0 3px' }}>{p.practitionerName}</p>
                          {p.tagline && (
                            <p style={{
                              fontSize: '12px', color: '#6B7280', margin: 0, lineHeight: 1.4,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{p.tagline}</p>
                          )}
                        </div>
                      </div>

                      {/* Services pills */}
                      {visibleServices.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', overflow: 'hidden', flexWrap: 'nowrap' }}>
                          {visibleServices.map(s => (
                            <span key={s} style={{
                              padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 500,
                              background: '#F3F0FF', color: '#7c3aed', whiteSpace: 'nowrap', flexShrink: 0,
                            }}>{s}</span>
                          ))}
                          {extraCount > 0 && (
                            <span style={{ fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap', alignSelf: 'center' }}>+{extraCount} more</span>
                          )}
                        </div>
                      )}

                      {/* Location + rating */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
                          {p.location || 'Virtual'}
                        </p>
                        {p.averageRating > 0 && (
                          <span style={{ fontSize: '12px', color: '#bb9749', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                            ★ {p.averageRating.toFixed(1)}
                          </span>
                        )}
                      </div>

                      {/* Spacer to push actions to bottom */}
                      <div style={{ flex: 1 }} />

                      {/* Actions */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingTop: '10px', borderTop: '1px solid #F3F4F6' }}>
                        {p.practitionerSlug && (
                          <span role="button" tabIndex={0}
                            onClick={() => navigate(`/site/${p.practitionerSlug}`)}
                            onKeyDown={(e) => e.key === 'Enter' && navigate(`/site/${p.practitionerSlug}`)}
                            style={{ fontSize: '13px', fontWeight: 500, color: '#bb9749', cursor: 'pointer', userSelect: 'none', padding: '4px 0' }}
                          >View profile</span>
                        )}
                        <div style={{ marginLeft: 'auto' }}>
                          <span role="button" tabIndex={0}
                            onClick={() => handleUnsave(p.practitionerId)}
                            onKeyDown={(e) => e.key === 'Enter' && handleUnsave(p.practitionerId)}
                            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', color: '#bb9749' }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="#bb9749" stroke="#bb9749" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes cshSpin { to { transform: rotate(360deg); } }
        @media (max-width: 480px) {
          .df-favorites-grid { grid-template-columns: 1fr !important; }
          @media (max-width: 767px) { .df-favorites-grid { grid-template-columns: 1fr !important; } }
        }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 64px)',
    background: '#F9FAFB',
    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  content: {
    flex: 1,
    maxWidth: '700px', margin: '0 auto', padding: '32px 20px', width: '100%', boxSizing: 'border-box',
  },
  goldBtn: {
    padding: '12px 28px', background: '#bb9749', color: '#fff',
    border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
  },
  filterSelect: {
    padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: '6px',
    fontSize: '12px', color: '#6B7280', background: '#fff',
    fontFamily: "'Montserrat', sans-serif", outline: 'none',
  },
  spinner: {
    width: '32px', height: '32px', border: '3px solid #E5E7EB', borderTopColor: '#bb9749',
    borderRadius: '50%', animation: 'cshSpin 0.8s linear infinite', margin: '0 auto',
  },
};

export default ClientSessionHub;
